import * as React from 'react';
import { PropTypes } from 'prop-types';
import CustomIcon from '../../../../common/CustomIcon';
import { CMI5, PASS_THRESHOLD, ONE_HUNDRED, ZERO, GRADEBOOK_SCOPE } from '../../../../../const';
import { CustomDonutChart } from '../../../../common/CustomDonutChart';
import { Paragraph } from '@netexknowledge/smart-components';
import Media from 'react-media';
import { MOBILE_MEDIA_QUERY } from '../../../../../utils/mobileUtils';
import * as activityService from '../../../../../services/domain/activityService';
import GradebookStudentSheet from '../../../mobile/GradebookStudentSheet';
import { getItemName, getStatusLabel, keepSheetOpen, takeKeptSheet } from '../../../mobile/gradebookMobileUtils';

export class GradeStudentRow extends React.Component {
  static propTypes = {
    columns: PropTypes.array,
    student: PropTypes.object,
    colors: PropTypes.object,
    avatarUrl: PropTypes.string,
    // Mobile sheet (see GradesList): drill-down handler of a unit or lesson column, undefined when it does not drill
    getDrillHandler: PropTypes.func,
    getLevelUpHandler: PropTypes.func,
    getAssessable: PropTypes.func,
    getOpenContentHandler: PropTypes.func,
    onToggleAssessable: PropTypes.func,
    onSheetClose: PropTypes.func,
    canEditAssessable: PropTypes.bool
  };

  constructor(props) {
    super(props);
    // Reopened on the new level after a level change made from this student's sheet
    const enterFrom = takeKeptSheet(props.student.userId);
    this.state = { sheetOpen: Boolean(enterFrom), enterFrom };
  }

  loadState() {
    let { columns, student, groups, selectedLesson, selectedUnit, selectedBook, gradebookScope } = this.props;

    const output = {
      success: ZERO,
      failed: ZERO,
      pending: ZERO,
      initialized: ZERO,
      uncompleted: ZERO,
      score: student.score,
      total: ZERO,
      gradeIcons: [],
      cells: []
    };

    const shouldShowStudent = (student, selectedActivity) => {
      const studentInGroup =
        selectedActivity.configuration.groups.length === ZERO ||
        (selectedActivity &&
          selectedActivity.configuration.groups.length > ZERO &&
          groups.find(e => e.id === selectedActivity.configuration.groups[ZERO]) &&
          groups.find(e => e.id === selectedActivity.configuration.groups[ZERO]).participants.includes(student.userId));
      const studentAssigned =
        selectedActivity.configuration.users.length === ZERO ||
        (selectedActivity &&
          selectedActivity.configuration.users.length > ZERO &&
          selectedActivity.configuration.users.includes(student.userId));

      return studentInGroup && studentAssigned;
    };

    columns.forEach((activity, index) => {
      const showStudent = selectedUnit || selectedBook ? shouldShowStudent(student, activity) : true;
      let color = '';
      let icon = '';

      if (activity.disabled) {
        color = 'uncomplete';
        icon = 'no-eye-view';
      }

      student.activities.forEach(grade => {
        if (activity.disabled) {
          color = 'uncomplete';
          icon = 'no-eye-view';
        } else if (!showStudent) {
          color = 'uncomplete';
          icon = 'unassign';
        } else if (activity.id === grade.activityId) {
          if (!activity.assessable && selectedLesson) {
            color = 'uncomplete';
            icon = 'no-evaluable';
            return;
          }
          if (grade.complete && !grade.pending) {
            let score = grade.score;
            if (score !== null) {
              if (grade.success) {
                color = 'complete';
                icon = 'check-icon';
                output.success += 1;
                output.total += 1;
              } else {
                color = 'wrong';
                icon = 'close-button';
                output.failed += 1;
                output.total += 1;
              }
              return;
            }
          } else if (grade.pending && Object.values(CMI5.OPEN_ACTIVITY_TYPE).includes(activity.activityType)) {
            color = 'pending';
            icon = 'help-button';
            output.pending += 1;
            output.total += 1;
          } else if (grade.initialized) {
            color = 'in-progress';
            icon = 'progress';
            output.initialized += 1;
            output.total += 1;
          } else {
            color = 'uncomplete';
            icon = 'dots-icon';
            output.uncompleted += 1;
            output.total += 1;
          }
        }
      });

      output.gradeIcons.push(<CustomIcon name={icon} classes={`grade ${color}`} size={24} key={index} />);
      output.cells.push({ item: activity, icon, color });
    });

    switch (gradebookScope) {
      case GRADEBOOK_SCOPE.ACTIVITIES:
        output.success = student.passStats;
        output.failed = student.failedStats;
        output.pending = student.pendingStats;
        output.initialized = 0;
        output.uncompleted = 100 - student.failedStats - student.passStats - student.pendingStats;
        break;
      case GRADEBOOK_SCOPE.UNIT_LESSONS:
        output.success = output.total ? (output.success * 100) / output.total : 0;
        output.failed = output.total ? (output.failed * 100) / output.total : 0;
        output.pending = output.total ? (output.pending * 100) / output.total : 0;
        output.initialized = output.total ? (output.initialized * 100) / output.total : 0;
        output.uncompleted = output.total ? (output.uncompleted * 100) / output.total : 100;
        break;
      default:
        output.success = 0;
        output.failed = 0;
        output.pending = 0;
        output.initialized = 0;
        output.uncompleted = 0;
    }

    return output;
  }

  getColor(grade, initialized, uncompleted, pending) {
    if (parseInt(grade) === ZERO) return 'gray-bg';

    if (initialized > ZERO || uncompleted > ZERO || pending > ZERO) return 'orange-bg';

    return grade >= PASS_THRESHOLD ? 'green-bg' : 'red-bg';
  }

  openSheet = () => this.setState({ sheetOpen: true });

  handleRowKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openSheet();
    }
  };

  // Changes the level keeping this student's sheet open (the rows reload)
  changeLevel(handler, direction) {
    if (!handler) return undefined;
    return () => {
      keepSheetOpen(this.props.student.userId, direction);
      this.setState({ sheetOpen: false });
      handler();
    };
  }

  getSheetRows(cells) {
    const { getDrillHandler, getLevelUpHandler, getAssessable, getOpenContentHandler } = this.props;
    const levelUp = getLevelUpHandler && getLevelUpHandler();
    return cells.map(({ item, icon, color }, index) => {
      const drill = getDrillHandler && getDrillHandler(item);
      const activity = getAssessable && getAssessable(item);
      return {
        key: index,
        typeIcon: activityService.getActivityIcon(item.activityType),
        name: getItemName(item),
        label: getStatusLabel(icon),
        statusNode: <CustomIcon name={icon} classes={`grade ${color}`} size={24} />,
        onClick: this.changeLevel(drill, 'down'),
        onLevelUp: drill ? this.changeLevel(levelUp, 'up') : undefined,
        assessable: activity,
        onOpenContent: activity && getOpenContentHandler ? getOpenContentHandler(item) : undefined
      };
    });
  }

  renderMobile() {
    const { student, avatarUrl } = this.props;
    const { success, failed, pending, initialized, uncompleted, cells, score } = this.loadState();
    const grade = (score * ONE_HUNDRED).toFixed(2);
    const gradeText = grade.toString().replace('.', ',');
    const chipClass = this.getColor(grade, initialized, uncompleted, pending);
    const studentNameSurname = `${student.name} ${student.surName}`;

    return (
      <>
        <div
          role="button"
          tabIndex={0}
          className={student.enabled ? 'student-row' : 'student-row opacity-20'}
          aria-haspopup="dialog"
          onClick={this.openSheet}
          onKeyDown={this.handleRowKeyDown}
        >
          <div className="student-name">
            <img src={avatarUrl} className="student-avatar" alt="" />
            <div className="student-chart">
              <CustomDonutChart
                success={success}
                failed={failed}
                pending={pending}
                initialized={initialized}
                uncompleted={uncompleted}
                hideText={true}
                donutWidth={3}
                diameter={57}
              />
            </div>
            <span className="student-name-text">{studentNameSurname}</span>
            <div className={`gradebook-custom-chip ${chipClass}`}>{gradeText}</div>
            <i className="custom-icons-right-chevron student-row-chevron" aria-hidden="true" />
          </div>
        </div>
        <GradebookStudentSheet
          open={this.state.sheetOpen}
          onClose={() => {
            this.setState({ sheetOpen: false });
            if (this.props.onSheetClose) this.props.onSheetClose();
          }}
          name={studentNameSurname}
          avatarUrl={avatarUrl}
          grade={gradeText}
          chipClass={chipClass}
          rows={this.getSheetRows(cells)}
          enterFrom={this.state.enterFrom}
          canEditAssessable={this.props.canEditAssessable}
          onToggleAssessable={this.props.onToggleAssessable}
        />
      </>
    );
  }

  render() {
    return (
      <Media query={MOBILE_MEDIA_QUERY}>{isMobile => (isMobile ? this.renderMobile() : this.renderDesktop())}</Media>
    );
  }

  renderDesktop() {
    let { student, avatarUrl } = this.props;
    const { success, failed, pending, initialized, uncompleted, gradeIcons, score } = this.loadState();
    let grade = (score * ONE_HUNDRED).toFixed(2);
    let studentNameSurname = `${student.name} ${student.surName} `;

    return (
      <div className={student.enabled ? 'student-row' : 'student-row opacity-20'}>
        <div className="student-name">
          <img ref={`studentImg${student.name}`} src={avatarUrl} className="student-avatar" alt="presentation" />
          <div className="student-chart">
            <CustomDonutChart
              success={success}
              failed={failed}
              pending={pending}
              initialized={initialized}
              uncompleted={uncompleted}
              hideText={true}
              donutWidth={3}
              diameter={57}
              showTooltips={true}
            />
          </div>
          <Paragraph className="text-grey-700 text--1line tooltip top" dataText={studentNameSurname}>
            <span>{student.name}</span>
          </Paragraph>
          <div className={`gradebook-custom-chip ${this.getColor(grade, initialized, uncompleted, pending)}`}>
            {grade.toString().replace('.', ',')}
          </div>
        </div>
        {Boolean(gradeIcons && gradeIcons.length) && gradeIcons.map(gradeIcon => gradeIcon)}
      </div>
    );
  }
}

export default GradeStudentRow;
